/**
 * The break engine, ported line for line from the approved prototype (which itself came from the
 * workbook's Module2: GenerateShiftsAndBreaks, FindBestBreakSlot, OptimizeBreakDensity, IsValidSlide,
 * with the beta11 fix). The prototype is the reference: test/breaks.test.ts runs the prototype's own code
 * on the same days and checks this gives exactly the same breaks.
 *
 * Rows are the stored form: 96 characters, '.' off, 'W' working, 'B' 15-minute break, 'L' 30-minute
 * lunch (two blocks).
 *
 * Cost of a layout (lower is better):
 *   evenness : 30 x (stretch length - even length)^2 for every work stretch
 *   crowding : 20 x (people on break)^3 per 15-minute block, + 100,000 per person over the 12% limit
 *   managers : 5,000,000 for any two managers off together, or the only manager + only lead
 *   hard     : every work stretch at least 1 h 15 min and at most 3 hours (1,000,000 per block over),
 *              and a last repair step re-places any shift that still misses it (owner 10/21)
 */
import { BLOCKS_PER_DAY as N } from './time.js';
const W_EVEN = 30;
/** Shortest stretch of work before or after a break: 5 quarter-hours = 1 h 15 min. */
export const MIN_GAP = 5;
/** Longest stretch of work without a break: 12 quarter-hours = 3 hours. */
export const MAX_GAP = 12;
const isBrk = (v) => v === 'B' || v === 'L';
const on = (v) => v !== '.';
/** Which breaks a stretch of this many hours gets: R = 15-minute break, M = 30-minute lunch. */
export function breaksForHours(h) {
    if (h > 2 && h < 6)
        return ['R'];
    if (Math.abs(h - 6) < 0.01)
        return ['R', 'M'];
    if (h > 6 && h <= 10)
        return ['R', 'M', 'R'];
    if (h > 10 && h < 14)
        return ['R', 'M', 'R', 'R'];
    if (Math.abs(h - 14) < 0.01)
        return ['R', 'M', 'R', 'M', 'R'];
    if (h > 14 && h <= 18)
        return ['R', 'M', 'R', 'R', 'M', 'R'];
    if (h > 18 && h < 22)
        return ['R', 'M', 'R', 'R', 'M', 'R', 'R'];
    if (Math.abs(h - 22) < 0.01)
        return ['R', 'M', 'R', 'M', 'R', 'M', 'R', 'R'];
    if (h > 22)
        return ['R', 'M', 'R', 'R', 'M', 'R', 'R', 'M', 'R'];
    return [];
}
function breaksIn(row, a, b) {
    const list = [];
    let c = a;
    while (c <= b) {
        if (row[c] === 'B') {
            list.push([c, 1]);
            c += 1;
        }
        else if (row[c] === 'L') {
            list.push([c, 2]);
            c += 2;
        }
        else
            c += 1;
    }
    return list;
}
/** Work stretches as [first, last] blocks (inclusive), as in the prototype. */
function runsOf(row) {
    const out = [];
    let c = 0;
    while (c < N) {
        if (!on(row[c])) {
            c++;
            continue;
        }
        const a = c;
        while (c < N && on(row[c]))
            c++;
        out.push([a, c - 1]);
    }
    return out;
}
function evenCost(row) {
    let cost = 0;
    for (const [a, b] of runsOf(row)) {
        const br = breaksIn(row, a, b);
        if (!br.length)
            continue;
        let brkBlocks = 0;
        for (const x of br)
            brkBlocks += x[1];
        const ideal = ((b - a + 1) - brkBlocks) / (br.length + 1);
        let prev = a;
        for (const x of br) {
            const L = x[0] - prev;
            cost += W_EVEN * (L - ideal) * (L - ideal);
            if (L < MIN_GAP)
                cost += 1e6 * (MIN_GAP - L);
            if (L > MAX_GAP)
                cost += 1e6 * (L - MAX_GAP);
            prev = x[0] + x[1];
        }
        const Lend = b + 1 - prev;
        cost += W_EVEN * (Lend - ideal) * (Lend - ideal);
        if (Lend < MIN_GAP)
            cost += 1e6 * (MIN_GAP - Lend);
        if (Lend > MAX_GAP)
            cost += 1e6 * (Lend - MAX_GAP);
    }
    return cost;
}
function colCost(g, c, rank, mgrs, leads, cap) {
    let d = 0, m = 0, mgrOff = false, leadOff = false;
    for (let r = 0; r < g.length; r++) {
        if (!isBrk(g[r][c]))
            continue;
        d++;
        if (rank[r] >= 7) {
            m++;
            mgrOff = true;
        }
        if (rank[r] === 6)
            leadOff = true;
    }
    let cost = 20 * d * d * d;
    if (d > cap)
        cost += 1e5 * (d - cap);
    if (m >= 2)
        cost += 5e6 * (m - 1); // never two managers on break at once
    if (mgrs === 1 && leads === 1 && mgrOff && leadOff)
        cost += 5e6;
    return cost;
}
function placeEven(row, a, b, ev, jitter) {
    let brkBlocks = 0;
    for (const e of ev)
        brkBlocks += e === 'R' ? 1 : 2;
    const work = (b - a + 1) - brkBlocks;
    const seg = work / (ev.length + 1);
    let pos = a, used = 0;
    for (let k = 0; k < ev.length; k++) {
        const len = ev[k] === 'R' ? 1 : 2;
        let start = a + Math.round(seg * (k + 1)) + used + jitter;
        start = Math.max(pos + MIN_GAP, Math.min(start, b - len + 1 - MIN_GAP * (ev.length - k)));
        if (ev[k] === 'R')
            row[start] = 'B';
        else {
            row[start] = 'L';
            row[start + 1] = 'L';
        }
        used += len;
        pos = start + len;
    }
}
/** The prototype's random numbers (mulberry32), so the same seed gives the same breaks. */
export function rng(seed) {
    let t = seed >>> 0;
    return () => {
        t += 0x6D2B79F5;
        let r = Math.imul(t ^ (t >>> 15), 1 | t);
        r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
        return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
}
function refine(g, movable, rank, mgrs, leads, cap, rand) {
    const rows = [];
    for (let r = 0; r < g.length; r++)
        if (movable[r])
            rows.push(r);
    for (let pass = 0; pass < 12; pass++) {
        let improved = false;
        for (let i = rows.length - 1; i > 0; i--) {
            const j = Math.floor(rand() * (i + 1));
            const t = rows[i];
            rows[i] = rows[j];
            rows[j] = t;
        }
        for (const r of rows) {
            const row = g[r];
            for (const run of runsOf(row)) {
                for (const br of breaksIn(row, run[0], run[1])) {
                    const s = br[0], len = br[1], v = row[s];
                    const baseRow = evenCost(row);
                    let best = null, bestDelta = -0.5;
                    for (let dlt = -6; dlt <= 6; dlt++) {
                        if (!dlt)
                            continue;
                        const ns = s + dlt;
                        if (ns < run[0] || ns + len - 1 > run[1])
                            continue;
                        const lo = Math.min(s, ns), hi = Math.max(s, ns) + len - 1;
                        let ok = true;
                        for (let c = lo; c <= hi; c++) {
                            if ((c < s || c >= s + len) && row[c] !== 'W') {
                                ok = false;
                                break;
                            }
                        }
                        if (!ok)
                            continue;
                        const cols = new Set();
                        for (let q = 0; q < len; q++) {
                            cols.add(s + q);
                            cols.add(ns + q);
                        }
                        // same order of adding up as the prototype (object keys: ascending numbers)
                        const keys = [...cols].sort((x, y) => x - y);
                        let before = 0, after = 0;
                        for (const key of keys)
                            before += colCost(g, key, rank, mgrs, leads, cap);
                        for (let q = 0; q < len; q++)
                            row[s + q] = 'W';
                        for (let q = 0; q < len; q++)
                            row[ns + q] = v;
                        for (const key of keys)
                            after += colCost(g, key, rank, mgrs, leads, cap);
                        const delta = (after - before) + (evenCost(row) - baseRow);
                        for (let q = 0; q < len; q++)
                            row[ns + q] = 'W';
                        for (let q = 0; q < len; q++)
                            row[s + q] = v;
                        if (delta < bestDelta) {
                            bestDelta = delta;
                            best = ns;
                        }
                    }
                    if (best !== null) {
                        for (let q = 0; q < len; q++)
                            row[s + q] = 'W';
                        for (let q = 0; q < len; q++)
                            row[best + q] = v;
                        improved = true;
                    }
                }
            }
        }
        if (!improved)
            break;
    }
}
/** True when every work stretch in a..b is 1 h 15 min to 3 h. */
function runFits(row, a, b) {
    let prev = a, ok = true;
    for (const x of breaksIn(row, a, b)) {
        const L = x[0] - prev;
        if (L < MIN_GAP || L > MAX_GAP)
            ok = false;
        prev = x[0] + x[1];
    }
    const Lend = b + 1 - prev;
    return ok && Lend >= MIN_GAP && Lend <= MAX_GAP;
}
/**
 * Last step (owner 10/21): any work stretch still under 1 h 15 min or over 3 h gets that shift's breaks
 * placed again, choosing only among layouts that keep every stretch inside the rule. Among those it takes
 * the cheapest by the balancer's own costs (evenness, crowding, managers), with everyone else fixed.
 * Shifts already inside the rule are left as they are. Ties go to the earliest position. Same as the prototype.
 */
function repairRuns(g, movable, rank, mgrs, leads, cap) {
    for (let r = 0; r < g.length; r++) {
        if (!movable[r])
            continue;
        const row = g[r];
        for (const [a, b] of runsOf(row)) {
            const br = breaksIn(row, a, b);
            if (!br.length || runFits(row, a, b))
                continue;
            const lens = br.map((x) => x[1]), n = lens.length;
            let sum = 0;
            for (const x of lens)
                sum += x;
            const work = (b - a + 1) - sum;
            if (work < MIN_GAP * (n + 1) || work > MAX_GAP * (n + 1))
                continue; // no layout can fit the rule
            const ideal = work / (n + 1);
            const old = row.slice(a, b + 1);
            for (let c = a; c <= b; c++)
                row[c] = 'W';
            const gapCost = (L) => W_EVEN * (L - ideal) * (L - ideal);
            // what putting break k at s adds to the crowding/manager cost of its blocks
            const brkCost = (k, s) => {
                const v = lens[k] === 1 ? 'B' : 'L';
                let before = 0, after = 0;
                for (let q = 0; q < lens[k]; q++)
                    before += colCost(g, s + q, rank, mgrs, leads, cap);
                for (let q = 0; q < lens[k]; q++)
                    row[s + q] = v;
                for (let q = 0; q < lens[k]; q++)
                    after += colCost(g, s + q, rank, mgrs, leads, cap);
                for (let q = 0; q < lens[k]; q++)
                    row[s + q] = 'W';
                return after - before;
            };
            // best[k].get(s): cheapest way to place breaks 0..k with break k starting at s; from: where k-1 started
            const best = [], from = [];
            for (let k = 0; k < n; k++) {
                best[k] = new Map();
                from[k] = new Map();
                for (let s = a; s + lens[k] - 1 <= b; s++) {
                    let bc = null, bp = null;
                    if (k === 0) {
                        if (s - a >= MIN_GAP && s - a <= MAX_GAP)
                            bc = gapCost(s - a);
                    }
                    else {
                        for (let p = s - lens[k - 1] - MAX_GAP; p <= s - lens[k - 1] - MIN_GAP; p++) {
                            const prev = best[k - 1].get(p);
                            if (prev === undefined)
                                continue;
                            const t = prev + gapCost(s - p - lens[k - 1]);
                            if (bc === null || t < bc) {
                                bc = t;
                                bp = p;
                            }
                        }
                    }
                    if (bc === null)
                        continue;
                    best[k].set(s, bc + brkCost(k, s));
                    from[k].set(s, bp);
                }
            }
            let end = null, endCost = null;
            for (let s = a; s <= b; s++) {
                const v = best[n - 1].get(s);
                if (v === undefined)
                    continue;
                const Lend = b + 1 - (s + lens[n - 1]);
                if (Lend < MIN_GAP || Lend > MAX_GAP)
                    continue;
                const tot = v + gapCost(Lend);
                if (endCost === null || tot < endCost) {
                    endCost = tot;
                    end = s;
                }
            }
            if (end === null) {
                for (let c = a; c <= b; c++)
                    row[c] = old[c - a];
                continue;
            }
            const at = [];
            for (let k = n - 1; k >= 0; k--) {
                at[k] = end;
                end = from[k].get(end) ?? null;
            }
            for (let k = 0; k < n; k++)
                for (let q = 0; q < lens[k]; q++)
                    row[at[k] + q] = lens[k] === 1 ? 'B' : 'L';
        }
    }
}
function ranks(g, levels) {
    let total = 0, mgrs = 0, leads = 0;
    const rank = [];
    for (let r = 0; r < g.length; r++) {
        rank[r] = 0;
        if (g[r].some(on)) {
            total++;
            if (levels[r] >= 7) {
                rank[r] = levels[r];
                mgrs++;
            }
            else if (levels[r] === 6) {
                rank[r] = 6;
                leads++;
            }
        }
    }
    return { rank, mgrs, leads, cap: Math.max(1, Math.floor(total * 0.12)) };
}
const toGrid = (rows) => rows.map((r) => r.split(''));
const fromGrid = (g) => g.map((r) => r.join(''));
/**
 * Places breaks and lunches for one store's day.
 *  - rows: everyone at the store that day (their whole rows; people off are all '.')
 *  - levels: each row's break level: 7–9 if they can take the Manager spot, 6 for the Shift Lead spot,
 *    otherwise their level up to 5 (see breakLevel)
 *  - edited: rows that changed; only these get new breaks, everyone else keeps theirs
 *  - forceAll: new breaks for everyone ("Recalculate all breaks")
 *  - seed: changes the starting arrangement
 */
export function generateBreaks(rows, levels, edited, forceAll, seed) {
    const ed = new Set(edited);
    const g = toGrid(rows);
    const movable = [];
    for (let r = 0; r < g.length; r++) {
        movable[r] = forceAll || ed.has(r);
        if (movable[r])
            for (let c = 0; c < N; c++)
                if (isBrk(g[r][c]))
                    g[r][c] = 'W';
    }
    const { rank, mgrs, leads, cap } = ranks(g, levels);
    const rand = rng((seed || 1) * 7919 + 13);
    for (let r = 0; r < g.length; r++) {
        if (!movable[r])
            continue;
        for (const run of runsOf(g[r])) {
            const ev = breaksForHours((run[1] - run[0] + 1) / 4);
            if (ev.length)
                placeEven(g[r], run[0], run[1], ev, Math.floor(rand() * 3) - 1);
        }
    }
    refine(g, movable, rank, mgrs, leads, cap, rand);
    repairRuns(g, movable, rank, mgrs, leads, cap);
    return fromGrid(g);
}
/** Total cost of a day's layout (the terms the balancer uses), for comparing alternatives. */
export function layoutCost(rows, levels) {
    const g = toGrid(rows);
    const { rank, mgrs, leads, cap } = ranks(g, levels);
    let cost = 0;
    for (let c = 0; c < N; c++)
        cost += colCost(g, c, rank, mgrs, leads, cap);
    for (const row of g)
        cost += evenCost(row);
    return cost;
}
/**
 * "Recalculate all breaks": tries 8 fresh arrangements (seeds seed+1 … seed+8) and keeps the cheapest one
 * that differs from what's there now. Returns null when every try gives the same layout.
 * The next seed to use afterwards is `usedSeed + 8`.
 */
export function recalcAll(rows, levels, seed) {
    const curKey = rows.join('|');
    let best = null, bestCost = Infinity, used = seed;
    for (let k = 1; k <= 8; k++) {
        const g = generateBreaks(rows, levels, [], true, seed + k);
        if (g.join('|') === curKey)
            continue;
        const cost = layoutCost(g, levels);
        if (cost < bestCost) {
            bestCost = cost;
            best = g;
            used = seed + k;
        }
    }
    return best ? { rows: best, usedSeed: used } : null;
}
/**
 * The level the break engine uses for someone (prototype `levels()`): if they can take the Manager spot,
 * 7–9; else if they can take the Shift Lead spot, 6; else their level, at most 5.
 */
export function breakLevel(level, canManagerSpot, canLeadSpot) {
    if (canManagerSpot)
        return Math.max(7, Math.min(9, level));
    if (canLeadSpot)
        return 6;
    return Math.min(5, level);
}
/** Paints blocks a..b (either order) on or off; painting on makes 'W', off makes '.'. */
export function paintRange(row, from, to, value) {
    const a = Math.min(from, to), b = Math.max(from, to);
    const r = row.split('');
    for (let k = a; k <= b; k++)
        r[k] = value ? 'W' : '.';
    return r.join('');
}
//# sourceMappingURL=breaks.js.map